/**
 * 頁 11 — 庫存帳本（P1-6）
 *
 * · `stock_movements` 係**唯一真相源**（append-only）：呢頁只讀，冇任何修改入口
 *   （要更正 → 另開一筆 `ADJUST`，唔准改舊行 —— `rules §七 B1`）
 * · 新 → 舊（`seq_no DESC`）；`limit` 最多 200（後端把關）
 * · 供應商只會見到自己貨品嘅移動（後端 scope）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import FormSelectField from '@coeldery/form-select-field';
import { btnGhost, card, hint as hintStyle, input as inputStyle, label as labelStyle } from '@coeldery/ui-kit';
import { LocationChip, MovementTypeChip, movementTypeOptions } from '../components/StockChips';
import { ApiError, api, type Me, type Movement } from '../lib/api';

const PAGE = 50;

const fmt = (n: number): string => String(Math.round(n * 1e6) / 1e6);

/** `2026-09-25 14:03:00` → 顯示用（保留原字串，唔做時區換算：值係事件時間） */
const when = (iso: string): string => iso.replace('T', ' ').slice(0, 16);

export default function Movements({ user }: { user: Me }) {
  const { t } = useTranslation();
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<Movement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.movements({
        type: type || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: PAGE,
        offset,
      });
      setRows(res.movements ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [type, from, to, offset, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 篩選一改 → 由第一頁開始（唔係嘅話可以停喺空白頁） */
  const changeFilter = (fn: () => void) => {
    setOffset(0);
    fn();
  };

  const typeOptions = [{ value: '', label: t('mov.allTypes') }, ...movementTypeOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))];
  const pageNo = Math.floor(offset / PAGE) + 1;
  const lastPage = Math.max(1, Math.ceil(total / PAGE));

  return (
    <>
      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('mov.title')}</h2>
      <p style={hintStyle}>{t('mov.intro')}</p>

      <div style={{ margin: '12px 0' }}>
        <FormSelectField
          id="f-mov-type"
          labelKey="mov.filterType"
          value={type}
          onChange={(v) => changeFilter(() => setType(v))}
          options={typeOptions}
        />
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '0 0 12px' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>
          {t('mov.from')}
          <input
            type="date"
            value={from}
            onChange={(e) => changeFilter(() => setFrom(e.target.value))}
            style={inputStyle}
          />
        </label>
        <label style={{ ...labelStyle, marginBottom: 0 }}>
          {t('mov.to')}
          <input
            type="date"
            value={to}
            onChange={(e) => changeFilter(() => setTo(e.target.value))}
            style={inputStyle}
          />
        </label>
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => void load()}
            style={{ border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {t('common.refresh')}
          </button>
        </p>
      )}

      {!loading && rows.length === 0 && !error && (
        <EmptyState titleKey="mov.emptyTitle" bodyKey="mov.emptyBody" />
      )}

      {rows.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((m) => (
            <li key={m.id} style={{ ...card, padding: '12px 14px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={hintStyle}>#{m.seqNo}</span>
                <MovementTypeChip type={m.type} />
                <strong>{m.itemName}</strong>
              </div>
              <p style={hintStyle}>
                {m.cartons > 0 ? `${t('inv.cartons')} ${m.cartons}` : ''}
                {m.units > 0 ? ` · ${t('inv.units')} ${m.units}` : ''} · cbm {fmt(m.cbm)} · {when(m.occurredAt)}
              </p>
              <p style={{ ...hintStyle, display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <LocationChip location={m.fromLoc} />
                <span aria-hidden="true">→</span>
                <LocationChip location={m.toLoc} />
                {m.destinationCode ? <span>· {m.destinationCode}</span> : ''}
              </p>
              <p style={hintStyle}>
                {user.role === 'supplier' ? '' : `${m.supplierCode} · `}
                {m.batchNo ? `${t('inv.batch')} ${m.batchNo} · ` : ''}
                {t('mov.actor')}：{m.actorName ?? '—'} · {t('mov.ref')}：{m.refType}
                {m.refId ? ` ${m.refId.slice(0, 8)}…` : ''}
              </p>
              {m.note && <p style={hintStyle}>📝 {m.note}</p>}
            </li>
          ))}
        </ul>
      )}

      {total > PAGE && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px' }}>
          <button
            type="button"
            style={btnGhost}
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            {t('mov.prev')}
          </button>
          <span style={hintStyle}>{t('mov.pageInfo', { page: pageNo, last: lastPage, total })}</span>
          <button
            type="button"
            style={btnGhost}
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            {t('mov.next')}
          </button>
        </div>
      )}
    </>
  );
}
